import { getGenreList } from './genres';
import { ChannelGuideTaxonomy } from './interfaces';
import { categoryMap, getCategoryDisplayName, getGenreCategory } from './utils';

export interface ChannelGuideSelection {
    category: string;
    genre: string;
    genreLabel: string;
}

const defaultSelection: ChannelGuideSelection = {
    category: 'All',
    genre: 'All',
    genreLabel: '',
};

export function toChannelGuideSlug(value: string) {
    return value
        .normalize('NFKD')
        .replace(/[\u0300-\u036f]/g, '')
        .replace(/['’]/g, '')
        .replace(/&/g, ' and ')
        .toLowerCase()
        .trim()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-+|-+$/g, '');
}

function matchesParam(param: string, aliases: string[]) {
    const slug = toChannelGuideSlug(param);

    return aliases.some((alias) => toChannelGuideSlug(alias) === slug);
}

export function resolveChannelGuideSelection(
    categoryParam: string | null,
    genreParam: string | null,
    taxonomy?: ChannelGuideTaxonomy,
): ChannelGuideSelection {
    if (!categoryParam) {
        return defaultSelection;
    }

    const categories = taxonomy
        ? taxonomy.categories.map((category) => ({
              key: category.fields.key,
              value: getCategoryDisplayName(category),
              aliases: [category.fields.key, category.fields.display_name, getCategoryDisplayName(category)],
          }))
        : Object.entries(categoryMap).map(([feedTitle, displayName]) => ({
              key: displayName,
              value: displayName,
              aliases: [feedTitle, displayName],
          }));
    const category = categories.find((item) => matchesParam(categoryParam, item.aliases));

    if (!category) {
        return defaultSelection;
    }

    const categorySelection = { ...defaultSelection, category: category.value };
    if (!genreParam || matchesParam(genreParam, ['All'])) {
        return categorySelection;
    }

    const genres = taxonomy
        ? taxonomy.genres
              .filter((genre) => getGenreCategory(genre)?.fields.key === category.key)
              .map((genre) => ({
                  value: genre.fields.display_name,
                  label: genre.fields.display_name,
                  aliases: [genre.fields.key, genre.fields.display_name],
              }))
        : (getGenreList(category.value) || [])
              .filter((genre) => genre.value !== 'All')
              .map((genre) => ({ ...genre, aliases: [genre.value, genre.label] }));
    const genre = genres.find((item) => matchesParam(genreParam, item.aliases));

    if (!genre) {
        return categorySelection;
    }

    return {
        category: category.value,
        genre: genre.value,
        genreLabel: genre.label,
    };
}
